import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Alert, Box, Card, LoadingOverlay, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useState } from 'react';
import { ProtectedComponent } from '@/features/auth/components/ProtectedComponent';
import { FallbackType, UserRole } from '@/features/auth/domain/auth.types';
import { useDisclosure } from '@mantine/hooks';
import { UserManagementHeader } from '@/features/users/components/UserManagement/UserManagementHeader';
import { UserSearchBar } from '@/features/users/components/UserSearch/UserSearchBar';
import { UserTable } from '@/features/users/components/UserManagement/UserTable';
import { UserPagination } from '@/features/users/components/UserSearch/UserPagination';
import { useListUsers } from '@/features/users/api/userQueries';
import { userSearchSchema } from "@/features/users/domain/user.schemas.ts";
import { UserDto } from "@/features/users/domain/user.types.ts";

import { BatchRoleManagementModal } from '@/features/users/components/RoleManagement/BatchRoleManagementModal';
import { AddUserModal } from '@/features/users/components/AddUser/AddUserModal';

export const Route = createFileRoute('/_authed/admin/manage-users/')({
  validateSearch: zodValidator(userSearchSchema),
  component: UserManagementPage,
  pendingComponent: () => (
    <Box size="xl" py="md">
      <LoadingOverlay visible/>
    </Box>
  ),
});

function UserManagementPage() {
  const searchParams = Route.useSearch();

  // Direct query - now using database users
  const {data, isLoading, isFetching, error} = useListUsers(searchParams);

  // Inline selection state - no separate hook since only used here
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(data?.users.map(u => u.id) || []));
  const clearSelection = () => setSelectedIds(new Set());

  // Calculate selected users inline
  const selectedUsers: UserDto[] = data?.users.filter(u => selectedIds.has(u.id)) || [];

  // Individual modal state management with useDisclosure
  const [addUserOpened, {open: openAddUser, close: closeAddUser}] = useDisclosure(false);
  const [batchRoleManagementOpened, {
    open: openBatchRoleManagement,
    close: closeBatchRoleManagement
  }] = useDisclosure(false);


  return (
    <ProtectedComponent
      requiredRole={UserRole.Admin}
      fallbackType={FallbackType.Page}
      fallbackTitle="User Management"
      fallbackAction="manage users"
    >
      <Box h="calc(100vh))" pos="relative">
        <Stack gap="lg">
          <UserManagementHeader
            totalCount={data?.totalCount || data?.users.length || 0}
            selectedCount={selectedUsers.length}
            isLoading={isLoading}
            onAddUser={openAddUser}
            onBatchRoleChange={openBatchRoleManagement}
            canManageUsers={true}
          />

          <Card radius="md" p="md">
            <Stack gap="md">
              <UserSearchBar isFetching={isFetching}/>
            </Stack>
          </Card>
          {error && (
            <Alert
              icon={<IconInfoCircle size={16}/>}
              title="Error Loading Users"
              color="red"
            >
              <Text size="sm">Failed to load users. Please try again.</Text>
            </Alert>
          )}

          <UserTable
            users={data?.users || []}
            isLoading={isLoading}
            isFetching={isFetching}
            selectedIds={selectedIds}
            onToggleSelection={toggleSelection}
            onSelectAll={selectAll}
            onClearSelection={clearSelection}
            canEditRoles={true}
          />

          {(data?.users.length || 0) > 0 && (
            <UserPagination
              isFetching={isFetching}
              hasNext={data?.hasNextPage}
              hasPrevious={data?.hasPreviousPage}
              currentCount={data?.users.length || 0}
              totalCount={data?.totalCount}
              currentPage={data?.currentPage || searchParams.page}
              totalPages={data?.totalPages}
            />
          )}
        </Stack>
      </Box>

      <AddUserModal
        opened={addUserOpened}
        onClose={closeAddUser}
      />

      <BatchRoleManagementModal
        opened={batchRoleManagementOpened}
        onClose={closeBatchRoleManagement}
        users={selectedUsers}
      />
    </ProtectedComponent>
  );
}